import { Outlet, useLocation } from "react-router-dom";
import React, { useContext, useEffect } from "react";

import GlobalConfig from "app/config/global";
import ManageTimeOffContext from "features/timesheet/contexts/ManageTimeOffContext";
import PageWrapper from "app/components/PageWrapper";
import SessionContext from "app/contexts/SessionContext";
import useIncompleteTimesheetApi from "features/timesheet/incomplete-timesheet/useIncompleteTimesheet";
import useManageUserTimeOff from "features/timesheet/manage-time-off/useManageUserTimeOff";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";

const TimesheetMainPage = () => {
    const session = useContext(SessionContext);
    const location = useLocation();
    const { useIncompleteTimesheet } = useIncompleteTimesheetApi();
    const { useManageUserTimeOffApi } = useManageUserTimeOff();
    const incompleteTimesheetData = useIncompleteTimesheet(session.legacyId);
    const userTimeOffRequests = useManageUserTimeOffApi(session.legacyId);
    const {
        upcomingPendingTimeOffRequestCount,
        setUpcomingPendingTimeOffRequestCount,
        setManageTimeOffRequestsData,
        setAreTimeOffRequestsLoading,
        setAreTimeOffRequestsRefetching,
    } = useContext(ManageTimeOffContext);

    useEffect(() => {
        setAreTimeOffRequestsLoading(userTimeOffRequests.isLoading);
        setAreTimeOffRequestsRefetching(userTimeOffRequests.isRefetching);

        if (!userTimeOffRequests.isLoading) {
            const currentDate = Date.now();
            const pendingTimeOffCount = userTimeOffRequests?.data?.filter(
                (element) =>
                    element.status === "Pending" &&
                    (currentDate < new Date(element.firstDayOut).getTime() ||
                        new Date().toDateString() ===
                            new Date(element.firstDayOut).toDateString())
            );

            setUpcomingPendingTimeOffRequestCount(pendingTimeOffCount?.length);
            setManageTimeOffRequestsData(userTimeOffRequests?.data);
        }
    }, [
        userTimeOffRequests,
        setAreTimeOffRequestsLoading,
        setAreTimeOffRequestsRefetching,
        setUpcomingPendingTimeOffRequestCount,
        setManageTimeOffRequestsData,
    ]);

    const isSmallScreen = useSmallBreakpoint();

    const count = incompleteTimesheetData.data?.length;
    const description = `${session.currentTitle}, ${
        session.currentDepartment
    } ${session.freelancer ? "(Freelancer)" : ""} ${
        session.contractor ? "(Contractor)" : ""
    }`;

    let tabs = [];

    if (!session.freelancer && !session.contractor) {
        let item = {
            id: "time-off",
            label: "Time Off",
            to: GlobalConfig.routes.timeOff,
        };
        tabs.splice(0, 0, item);
        item = {
            id: "manage-time-off",
            label: `Manage Time Off Requests ${
                upcomingPendingTimeOffRequestCount > 0
                    ? `(${upcomingPendingTimeOffRequestCount})`
                    : ""
            }`,
            to: GlobalConfig.routes.manageTimeOff,
        };
        tabs.splice(0, 0, item);
    }

    if (session.timesheetRequired) {
        const incompleteLabel = isSmallScreen
            ? "Incomplete"
            : "Incomplete Timesheet";
        let item = {
            id: "incomplete-timesheet",
            label:
                count > 0 ? `${incompleteLabel} (${count})` : incompleteLabel,
            to: GlobalConfig.routes.timesheetIncomplete,
        };
        tabs.splice(0, 0, item);
        item = {
            id: "timesheet",
            label: "Timesheet",
            to: GlobalConfig.routes.timesheetGrid,
        };
        tabs.splice(0, 0, item);
    }

    tabs.push({
        id: "holidays",
        label: "Holidays",
        to: GlobalConfig.routes.timesheetHolidays,
    });

    if (isSmallScreen) {
        const mobileTabIds = new Set([
            "timesheet",
            "incomplete-timesheet",
            "holidays",
        ]);
        tabs = tabs.filter((tab) => mobileTabIds.has(tab.id));
    }

    const isHolidaysTab =
        location.pathname ===
        `${GlobalConfig.routes.timesheetLandingPage}/${GlobalConfig.routes.timesheetHolidays}`;

    return (
        <PageWrapper
            isAlreadyAuthorize={isHolidaysTab}
            backgroundAuth
            title={session.fullName}
            tabsMarginBottom="10px"
            description={description}
            listOptions={[
                {
                    name: `${session.currentPayOffice}`,
                    icon: "office-pay",
                    text: "Pay Office",
                },
                {
                    name: `${session.currentSeattingOffice}`,
                    icon: "office-seat",
                    text: "Seat Office",
                },
            ]}
            tabs={tabs}
        >
            <Outlet />
        </PageWrapper>
    );
};

export default TimesheetMainPage;
